import { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import type { TabDefinition } from './TabBar';
import { federFlaeche, transition, uebergangSanft } from '../lib/motion';
import { cn } from '../lib/cn';

export interface MehrBlattProps {
  offen: boolean;
  bereiche: TabDefinition[];
  aktiv: string;
  onWahl: (id: string) => void;
  onSchliessen: () => void;
}

/**
 * Bereichsauswahl für kleine Geräte.
 *
 * Die untere Leiste trägt höchstens fünf Einträge. Alle weiteren Bereiche liegen in diesem
 * Blatt, das von unten einfährt. Schließt über Hintergrund, Escape oder die Auswahl eines
 * Bereichs.
 */
export function MehrBlatt({ offen, bereiche, aktiv, onWahl, onSchliessen }: MehrBlattProps) {
  const reduziert = useReducedMotion();
  const ersterKnopf = useRef<HTMLButtonElement | null>(null);
  // Aktuellen Handler in einer Ref halten: sonst würde jede neue Funktionsinstanz des
  // Aufrufers den Effekt erneut auslösen und den Fokus auf die erste Kachel zurücksetzen.
  const schliessen = useRef(onSchliessen);
  schliessen.current = onSchliessen;

  useEffect(() => {
    if (!offen) return;
    ersterKnopf.current?.focus();
    function beiTaste(e: KeyboardEvent) {
      if (e.key === 'Escape') schliessen.current();
    }
    window.addEventListener('keydown', beiTaste);
    return () => window.removeEventListener('keydown', beiTaste);
  }, [offen]);

  return (
    <AnimatePresence>
      {offen && (
        <div className="fixed inset-0 z-30 md:hidden">
          <motion.button
            type="button"
            aria-label="Bereichsauswahl schließen"
            className="absolute inset-0 h-full w-full cursor-default bg-fg/30"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={transition(!!reduziert, uebergangSanft)}
            onClick={onSchliessen}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Weitere Bereiche"
            className="absolute inset-x-0 bottom-0 rounded-t-2xl border-t border-border bg-surface shadow-lg sicherheitsabstand-unten"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={transition(!!reduziert, federFlaeche)}
          >
            <div className="mx-auto mt-2.5 h-1 w-10 rounded-pill bg-border" aria-hidden="true" />
            <p className="px-5 pb-2 pt-3 text-xs font-medium uppercase tracking-wide text-fg-muted">
              Weitere Bereiche
            </p>
            <div className="grid grid-cols-3 gap-2 px-4 pb-5">
              {bereiche.map((b, i) => {
                const istAktiv = b.id === aktiv;
                return (
                  <button
                    key={b.id}
                    ref={i === 0 ? ersterKnopf : undefined}
                    type="button"
                    aria-current={istAktiv ? 'page' : undefined}
                    onClick={() => onWahl(b.id)}
                    className={cn(
                      'flex min-h-[4.75rem] min-w-0 flex-col items-center justify-center gap-1.5 rounded-lg px-1.5 py-3',
                      'text-center text-xs font-medium transition-colors duration-fast ease-out',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
                      'active:scale-[0.97]',
                      istAktiv
                        ? 'bg-accent-soft text-accent-soft-fg'
                        : 'bg-surface-2 text-fg hover:bg-surface-3',
                    )}
                  >
                    <span className={istAktiv ? 'text-accent' : 'text-fg-muted'}>{b.icon}</span>
                    <span className="line-clamp-2 break-words leading-tight">{b.label}</span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
